import { createHash, randomInt, timingSafeEqual } from "node:crypto";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { buildMemberSetupCodeEmail } from "@/lib/email-templates";

const SETUP_CODE_TTL_MS = 15 * 60 * 1000;
const SETUP_CODE_MAX_REQUESTS = 3;
const SETUP_CODE_MAX_ATTEMPTS = 5;

function hashSetupCode(code: string): string {
  return createHash("sha256").update(code).digest("hex");
}

function hashesEqual(a: string, b: string): boolean {
  const left = Buffer.from(a);
  const right = Buffer.from(b);
  if (left.length !== right.length) return false;
  return timingSafeEqual(left, right);
}

function generateSetupCode(): string {
  return String(randomInt(0, 1_000_000)).padStart(6, "0");
}

async function findUnclaimedMemberByEmail(email: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  // Exact eq only — never ilike. LIKE treats `_`/`%` as wildcards, so
  // `john_doe@x.com` would match `johnXdoe@x.com` and enable setup ATO.
  // Emails are normalized to lowercase on write (ensureMember / signup).
  const { data: profile, error: profileErr } = await supabaseAdmin
    .from("profiles")
    .select("id")
    .eq("email", email)
    .maybeSingle();
  if (profileErr) throw new Error(profileErr.message);
  if (!profile) return null;

  const { data: authData, error: authErr } = await supabaseAdmin.auth.admin.getUserById(profile.id);
  if (authErr) throw new Error(authErr.message);
  const user = authData.user;
  if (!user) return null;
  if (user.last_sign_in_at) return null;

  return { profileId: profile.id };
}

/** Unauthenticated: email a one-time code to claim an admin-created member account. */
export const requestMemberSetupCode = createServerFn({ method: "POST" })
  .validator((d) => z.object({ email: z.string().trim().email().max(255) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = data.email.toLowerCase();

    // Always return the same shape so callers cannot probe eligibility.
    const ok = { ok: true as const };

    const unclaimed = await findUnclaimedMemberByEmail(email);
    if (!unclaimed) return ok;

    const windowStart = new Date(Date.now() - SETUP_CODE_TTL_MS).toISOString();
    const { count, error: countErr } = await supabaseAdmin
      .from("member_setup_codes")
      .select("id", { count: "exact", head: true })
      .eq("email", email)
      .gte("created_at", windowStart);
    if (countErr) throw new Error(countErr.message);
    if ((count ?? 0) >= SETUP_CODE_MAX_REQUESTS) return ok;

    const code = generateSetupCode();
    const expiresAt = new Date(Date.now() + SETUP_CODE_TTL_MS).toISOString();
    const { error: insertErr } = await supabaseAdmin.from("member_setup_codes").insert({
      email,
      code_hash: hashSetupCode(code),
      expires_at: expiresAt,
      attempt_count: 0,
    });
    if (insertErr) throw new Error(insertErr.message);

    const content = buildMemberSetupCodeEmail({ code });
    const { sendEmail } = await import("@/lib/email.server");
    await sendEmail({
      to: email,
      subject: content.subject,
      html: content.html,
      text: content.text,
    });

    return ok;
  });

export const finishMemberSetup = createServerFn({ method: "POST" })
  .validator((d) =>
    z
      .object({
        email: z.string().trim().email().max(255),
        password: z.string().min(6).max(128),
        fullName: z.string().trim().min(1).max(100).optional(),
        code: z
          .string()
          .trim()
          .regex(/^\d{6}$/, "Enter the 6-digit setup code from your email."),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = data.email.toLowerCase();

    const { data: profile, error: profileErr } = await supabaseAdmin
      .from("profiles")
      .select("id")
      .eq("email", email)
      .maybeSingle();
    if (profileErr) throw new Error(profileErr.message);
    if (!profile) {
      throw new Error("No account found for this email. Contact the pro shop to get set up.");
    }

    const { data: authData, error: authErr } = await supabaseAdmin.auth.admin.getUserById(
      profile.id,
    );
    if (authErr) throw new Error(authErr.message);
    const user = authData.user;
    if (!user)
      throw new Error("No account found for this email. Contact the pro shop to get set up.");

    if (user.last_sign_in_at) {
      throw new Error("This account is already set up. Sign in or use Forgot password.");
    }

    const { data: codeRows, error: codeErr } = await supabaseAdmin
      .from("member_setup_codes")
      .select("id, code_hash, expires_at, attempt_count")
      .eq("email", email)
      .order("created_at", { ascending: false })
      .limit(1);
    if (codeErr) throw new Error(codeErr.message);
    const codeRow = codeRows?.[0];
    if (!codeRow) {
      throw new Error("Request a setup code first, then enter the code from your email.");
    }
    if (codeRow.attempt_count >= SETUP_CODE_MAX_ATTEMPTS) {
      throw new Error("Too many incorrect setup code attempts. Request a new code.");
    }
    if (new Date(codeRow.expires_at).getTime() <= Date.now()) {
      throw new Error("That setup code has expired. Request a new code.");
    }

    const expected = hashSetupCode(data.code);
    if (!hashesEqual(expected, codeRow.code_hash)) {
      await supabaseAdmin
        .from("member_setup_codes")
        .update({ attempt_count: codeRow.attempt_count + 1 })
        .eq("id", codeRow.id);
      throw new Error("Invalid setup code. Check your email and try again.");
    }

    const { error: consumeErr } = await supabaseAdmin
      .from("member_setup_codes")
      .delete()
      .eq("email", email);
    if (consumeErr) throw new Error(consumeErr.message);

    const { error: updateErr } = await supabaseAdmin.auth.admin.updateUserById(profile.id, {
      password: data.password,
      ...(data.fullName ? { user_metadata: { full_name: data.fullName } } : {}),
    });
    if (updateErr) throw new Error(updateErr.message);

    if (data.fullName) {
      const { error: nameErr } = await supabaseAdmin
        .from("profiles")
        .update({ full_name: data.fullName })
        .eq("id", profile.id);
      if (nameErr) throw new Error(nameErr.message);
    }

    return { ok: true as const };
  });
